"use client";

import { useCallback, useEffect, useRef, useState } from "react";

const AUTO_DISMISS_DELAY_MS = 4000;

export interface UseAutoDismissErrorReturn {
  error: string | null;
  setError: (error: string | null) => void;
}

/**
 * Hook for managing an error state that auto-dismisses after a delay.
 * The error will automatically clear after 4 seconds.
 * Properly cleans up the timeout on unmount.
 */
export function useAutoDismissError(): UseAutoDismissErrorReturn {
  const [error, _setError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setError = useCallback((newError: string | null) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    _setError(newError);

    if (newError !== null) {
      errorTimeoutRef.current = setTimeout(() => {
        _setError(null);
      }, AUTO_DISMISS_DELAY_MS);
    }
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, []);

  return { error, setError };
}
